import { Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface AIProcessingBannerProps {
  /** The message to display (already translated) */
  message: string;
  /** Optional className override */
  className?: string;
}

/**
 * Shared banner shown while AI is processing (document parsing, domain enrichment, etc.).
 *
 * Displays an animated sparkles icon with a shimmer gradient sweep across the background
 * to clearly signal that AI is actively working.
 *
 * Usage:
 *   {isParsing && <AIProcessingBanner message={t('common:aiProcessing.analyzingDocument')} />}
 */
export function AIProcessingBanner({ message, className }: AIProcessingBannerProps) {
  return (
    <div
      className={cn(
        'ai-processing-banner relative overflow-hidden flex items-center gap-3 rounded-lg px-4 py-3',
        'bg-primary/[0.06] dark:bg-primary/[0.12] border border-primary/10',
        className,
      )}
    >
      {/* Shimmer sweep overlay */}
      <div className="ai-processing-shimmer pointer-events-none absolute inset-0 rounded-lg" />

      {/* Animated sparkles icon */}
      <Sparkles className="ai-processing-sparkle relative h-4 w-4 shrink-0 text-primary" />

      {/* Message */}
      <span className="relative text-sm font-medium text-foreground">
        {message}
      </span>
    </div>
  );
}
